import React, { ReactElement } from 'react';
import { Drawer, DrawerHeader, DrawerFooter } from '@digicert/dcone-common-ui';
import { Button, ButtonType } from '@digicert/dcone-common-ui';
import { Icon } from '@digicert/dcone-common-ui';
import { faGlobe, faArrowUpRightFromSquare, faServer, faChevronDown, faLink } from '@fortawesome/pro-light-svg-icons'
import { Environment } from './environmentsMock';
import { getCertCentralCounts } from './certCentralCount';
import { serviceIcon } from '../../shared/helpers/utils';
import { STRINGS } from './environmentsStrings';
import styles from './environments.module.scss';

interface EnvironmentSideRailProps {
  environment: Environment | null;
  show: boolean;
  onDismiss: () => void;
}

const isCertCentralApp = (appCode: string): boolean => appCode.startsWith('cc.');
const isTrustSourceApp = (appCode: string): boolean =>
  appCode.startsWith('cc.') || appCode.startsWith('cm.');

export const EnvironmentSideRail = ({
  environment,
  show,
  onDismiss,
}: EnvironmentSideRailProps): ReactElement | null => {
  if (!environment) return null;

  const { us, eu } = getCertCentralCounts(environment);
  const hasCertCentral = us > 0 || eu > 0;

  const certCentralApp = environment.applications.find((app) =>
    isCertCentralApp(app.app_code)
  );

  const trustSourceApps = environment.applications.filter(
    (app) => isTrustSourceApp(app.app_code) && !isCertCentralApp(app.app_code)
  );

  const productApps = environment.applications.filter(
    (app) => !isTrustSourceApp(app.app_code)
  );

  const handleAccessEnvironment = () => {
    // TODO: add access environment callback — open env URL or trigger navigation
  };

  const handleConnectCertCentral = () => {
    // TODO: add connect CertCentral callback
  };

  // TODO: overflow menu actions not defined yet — placeholder so the menu matches Figma without breaking on click
  const overflowMenuItems = [
    {
      key: 'access-environment',
      label: STRINGS.ACCESS_ENVIRONMENT,
      onClick: handleAccessEnvironment,
    },
  ];

  const headerIcon = (
    <div className={styles.avatarCircle}>
      {environment.name.charAt(0).toUpperCase()}
    </div>
  );

  const headerDescription = (
    <div className={styles.cardMeta}>
      <span className={styles.metaItem}>
        <Icon icon={faGlobe} size="sm" /> {environment.region}
      </span>
      {hasCertCentral && (
        <span className={styles.metaItem}>
          <Icon icon={faServer} size="sm" />
          {STRINGS.CERTCENTRAL_COUNT(us, eu)}
        </span>
      )}
    </div>
  );

  return (
    <Drawer
      open={show}
      onClose={onDismiss}
      width={760}
      header={
        <DrawerHeader
          icon={headerIcon}
          label={environment.name}
          description={headerDescription}
          overflowMenuItems={overflowMenuItems}
          closable
          onClose={onDismiss}
        />
      }
      footer={
        <DrawerFooter>
          <Button
            buttonType={ButtonType.PRIMARY}
            onClick={handleAccessEnvironment}
            icon={<Icon icon={faArrowUpRightFromSquare} />}
          >
            {STRINGS.ACCESS_ENVIRONMENT}
          </Button>
        </DrawerFooter>
      }
      content={
        <div className={styles.sideRailBody}>
          {/* ───── Trust Sources ───── */}
          <div className={styles.sectionGroup}>
            <h3 className={styles.sectionTitle}>{STRINGS.TRUST_SOURCES_LABEL}</h3>

            <div className={styles.itemsList}>
              {/* CertCentral trust source */}
              {certCentralApp && (
                <>
                  <div className={styles.accordionRow}>
                    <span className={styles.appIconBox}>{serviceIcon(certCentralApp.name)}</span>
                    <div className={styles.appTextBlock}>
                      <p className={styles.appName}>{STRINGS.CERTCENTRAL_SECTION_LABEL}</p>
                    </div>
                  </div>

                  <div className={styles.instancesList}>
                    {certCentralApp.service_provider_accounts?.map((spa) => (
                      <div key={spa.id} className={styles.instanceRow}>
                        <div className={styles.instanceAvatar}>
                          {spa.source_account_name.charAt(0).toUpperCase()}
                        </div>
                        <div className={styles.instanceText}>
                          <p className={styles.instanceName}>
                            {spa.source_account_name}{' '}
                            <span className={styles.instanceRegion}>
                              ({spa.source_account_friendly_identifier})
                            </span>
                          </p>
                          {spa.role && <p className={styles.instanceRole}>{spa.role}</p>}
                        </div>
                      </div>
                    ))}

                    <div className={styles.connectRow}>
                      <button className={styles.connectLink} onClick={handleConnectCertCentral}>
                        <Icon icon={faLink} size="sm" />
                        {STRINGS.CONNECT_CERTCENTRAL}
                        <Icon icon={faChevronDown} size="sm" />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Other trust source apps (e.g. Private CA) */}
              {trustSourceApps.map((app) => (
                <div key={app.id} className={styles.accordionRow}>
                  <span className={styles.appIconBox}>{serviceIcon(app.name)}</span>
                  <div className={styles.appTextBlock}>
                    <p className={styles.appName}>{app.name}</p>
                    <div className={styles.roleTags}>
                      {app.roles && app.roles.length > 0 ? (
                        app.roles.map((role) => (
                          <span key={role} className={styles.roleTag}>{role}</span>
                        ))
                      ) : (
                        <span className={styles.roleTag}>—</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.divider} />

          {/* ───── Products ───── */}
          <div className={styles.sectionGroup}>
            <h3 className={styles.sectionTitle}>{STRINGS.PRODUCTS_LABEL}</h3>

            <div className={styles.itemsList}>
              {productApps.map((app) => (
                <div key={app.id} className={styles.accordionRow}>
                  <span className={styles.appIconBox}>{serviceIcon(app.name)}</span>
                  <div className={styles.appTextBlock}>
                    <p className={styles.appName}>{app.name}</p>
                    <div className={styles.roleTags}>
                      {app.roles && app.roles.length > 0 ? (
                        app.roles.map((role) => (
                          <span key={role} className={styles.roleTag}>{role}</span>
                        ))
                      ) : (
                        <span className={styles.roleTag}>—</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
};